import instance from "..";

// https://pkg.go.dev/github.com/stripe/stripe-go/v72/invoice#Invoice
export type StripeInvoice = {
  id: string;
  object?: string;
  account_country?: string;
  account_name?: string;
  amount_due?: number;
  amount_paid?: number;
  amount_remaining?: number;
  application?: string;
  application_fee_amount?: number;
  attempt_count?: number;
  attempted?: boolean;
  auto_advance?: boolean;
  billing_reason?: string;
  charge?: string;
  collection_method?: string;
  created?: number;
  currency?: string;
  customer?: string;
  customer_email?: string;
  customer_name?: string;
  customer_phone?: string;
  customer_tax_exempt?: string;
  default_payment_method?: string;
  default_source?: string;
  description?: string;
  discount?: {
    coupon?: StripeCoupon;
    deleted?: boolean;
    end?: number;
    id?: string;
    invoice?: string;
    invoice_item?: string;
    start?: number;
    subscription?: string;
  };
  due_date?: number;
  ending_balance?: number;
  footer?: string;
  hosted_invoice_url?: string;
  invoice_pdf?: string;
  last_finalization_error?: {
    status?: number;
    message?: string;
    request_id?: string;
    type?: string;
  };
  livemode?: boolean;
  metadata?: Record<string, string>;
  next_payment_attempt?: number;
  number?: string;
  paid?: boolean;
  paid_out_of_band?: boolean;
  payment_intent?: string;
  period_end?: number;
  period_start?: number;
  post_payment_credit_notes_amount?: number;
  pre_payment_credit_notes_amount?: number;
  receipt_number?: string;
  starting_balance?: number;
  statement_descriptor?: string;
  status?: string;
  subscription?: string;
  subtotal?: number;
  tax?: number;
  tax_percent?: number;
  total?: number;
  total_discount_amounts?: {
    amount?: number;
  }[];
  webhooks_delivered_at?: number;
};

type BillingMethod = "stripe" | "brokerage" | "manual";

export type BillingBankAccount = {
  id: string;
  brand: string;
  last_4: string;
};

export type BillingCard = {
  id: string;
  brand: string;
  last_4: string;
  expiration: string;
};

export interface Billing {
  plan: string;
  lifetimeSubCount: number;
  method?: BillingMethod;
  is_yearly?: boolean;
  price?: number | null;
  address?: {
    name: string;
    phone: string;
    email: string;
    address: {
      city: string;
      country: string;
      line1: string;
      line2: string;
      postal_code: string;
      state: string;
    };
  };
  bank_accounts?: BillingBankAccount[];
  cards?: BillingCard[];
  stripe_invoices?: StripeInvoice[];
  brokerageInvoices?: {
    id: string;
    owner_id: string;
    subscription_plan_id: string;
    price: number;
    created_at: string;
    date_paid: string;
  }[];
  paymentIntentSecret?: string;
  setupIntentSecret?: string;
  trialDays?: number;
  nextPaymentDate?: string | null;
  fundamental_method?: BillingMethod;
  fundamental_plan?: string;
  fundamental_price?: number | null;
  fundamental_trialDays?: number;
  fundamental_nextPaymentDate?: string | null;
  otc_method?: BillingMethod;
  otc_plan?: string;
  otc_price?: number | null;
  otc_trialDays?: number;
  otc_nextPaymentDate?: string | null;
  logo_method?: BillingMethod;
  logo_plan?: string;
  logo_price?: number | null;
  logo_trialDays?: number;
  logo_nextPaymentDate?: string | null;
}

export const getBillingOverview = async (): Promise<Billing> => {
  const res = await instance.get("/billing/overview");
  return res.data;
};

/**
 * Price is in USD cents
 */
export type BillingPlan = {
  id: string;
  price: number;
};

export type BillingPlans = {
  monthly: BillingPlan;
  yearly: BillingPlan;
};

export const getBillingPlans = async () => {
  const res = await instance.get<BillingPlans>("/billing/plans");
  return res.data;
};

export interface CryptoFeeStructure {
  fee_structure: {
    [key: string]: {
      volume: string;
      maker: string;
      taker: string;
    };
  };
}

export const getCryptoFeeStructure = async (): Promise<CryptoFeeStructure> => {
  const res = await instance.get("/crypto/fee-structure");
  return res.data;
};

export interface CryptoMonthlyVolume {
  volume: string;
}

export const getCryptoMonthlyVolume = async (
  accountID: string,
): Promise<CryptoMonthlyVolume> => {
  const res = await instance.get(
    `/accounts/${accountID}/crypto/monthly-volume`,
  );
  return res.data;
};

// https://pkg.go.dev/github.com/stripe/stripe-go/v76#Coupon
export type StripeCoupon = {
  amount_off?: number;
  applies_to?: { products: string[] };
  created: number;
  currency: string;
  currency_options?: Record<string, { amount_off?: number }>;
  deleted?: boolean;
  duration: "forever" | "once" | "repeating";
  duration_in_months?: number;
  id: string;
  livemode?: boolean;
  max_redemptions?: number;
  metadata?: Record<string, string>;
  name?: string;
  object?: string;
  percent_off?: number;
  redeem_by?: number;
  times_redeemed?: number;
  valid: boolean;
};

export const getStripeCouponCodeValidity = async (couponCode: string) => {
  const res = await instance.get<StripeCoupon>(`/billing/coupon/${couponCode}`);
  return res.data;
};

export const createStripeSubscription = async ({
  token,
  planId,
  couponId,
}: {
  token: string;
  planId: string;
  couponId?: string;
}) => {
  const res = await instance.post("/billing/stripe", {
    token,
    plan: planId,
    coupon: couponId,
  });
  return res.data;
};

export const updateStripeSubscription = async (token: string) => {
  const res = await instance.patch("/billing/stripe", {
    token,
  });
  return res.data;
};

export const cancelStripeSubscription = async (reason: string) => {
  const res = await instance.delete("/billing/stripe", {
    data: {
      reason,
    },
  });
  return res.data;
};
